// ** Redux Imports
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import RatingService from "../../../../services/Web/webrating.service"
import { toastSuccess, toastError } from "../../../../utility/toastutill"

/**
 * ======================================================
 * CUSTOMER – GET PRODUCT RATINGS
 * ======================================================
 */
export const getProductRatings = createAsyncThunk(
  "rating/getProductRatings",
  async (productId) => {
    try {
      const res = await RatingService.getProductRatings(productId)
      return res.data?.ratings || []
    } catch (error) {
      toastError(error)
      return []
    }
  }
)

/**
 * ======================================================
 * CUSTOMER – ADD RATING
 * ======================================================
 */
export const addRating = createAsyncThunk(
  "rating/addRating",
  async ({ payload, productId }, { dispatch }) => {
    try {
      const res = await RatingService.addRating(payload)
      if (res.data?.message) {
        toastSuccess(res.data.message)
        dispatch(getProductRatings(productId))
      }
      return true
    } catch (error) {
      toastError(error)
      return false
    }
  }
)

/**
 * ======================================================
 * CUSTOMER – DELETE RATING
 * ======================================================
 */
export const deleteRating = createAsyncThunk(
  "rating/deleteRating",
  async ({ ratingId, productId }, { dispatch }) => {
    try {
      const res = await RatingService.deleteRating(ratingId)
      if (res.data?.message) {
        toastSuccess(res.data.message)
        dispatch(getProductRatings(productId))
      }
      return ratingId
    } catch (error) {
      toastError(error)
      return null
    }
  }
)

/**
 * ======================================================
 * ADMIN – GET ALL RATINGS
 * ======================================================
 */
export const getAllRatingsAdmin = createAsyncThunk(
  "rating/getAllRatingsAdmin",
  async ({ q = "" } = {}, { rejectWithValue }) => {
    try {
      const res = await RatingService.getAllRatingsAdmin(q)
      return res.data?.data || []
    } catch (error) {
      toastError(error)
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch ratings"
      )
    }
  }
)

/**
 * ======================================================
 * ADMIN – UPDATE RATING STATUS
 * ======================================================
 */
export const updateRatingStatus = createAsyncThunk(
  "rating/updateRatingStatus",
  async ({ ratingId, status }, { dispatch }) => {
    try {
      const res = await RatingService.updateRatingStatus(ratingId, {
        status
      })
      if (res.data?.success) {
        toastSuccess(res.data.message)
        dispatch(getAllRatingsAdmin({}))
      }
      return true
    } catch (error) {
      toastError(error)
      return false
    }
  }
)

/**
 * ======================================================
 * VENDOR – GET RATINGS FOR OWN PRODUCTS
 * ======================================================
 */
export const getVendorRatings = createAsyncThunk(
  "rating/getVendorRatings",
  async ({ q = "" } = {}) => {
    try {
      const res = await RatingService.getVendorRatings(q)
      return res.data?.data || []
    } catch (error) {
      toastError(error)
      return []
    }
  }
)

/**
 * ======================================================
 * SLICE
 * ======================================================
 */
export const ratingSlice = createSlice({
  name: "rating",
  initialState: {
    productRatings: [],
    adminRatings: [],
    vendorRatings: [],
    loading: false,
    error: null
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // PRODUCT RATINGS
      .addCase(getProductRatings.pending, (state) => {
        state.loading = true
      })
      .addCase(getProductRatings.fulfilled, (state, action) => {
        state.loading = false
        state.productRatings = action.payload
      })
      .addCase(getProductRatings.rejected, (state) => {
        state.loading = false
      })

      // ADMIN RATINGS
      .addCase(getAllRatingsAdmin.pending, (state) => {
        state.loading = true
      })
      .addCase(getAllRatingsAdmin.fulfilled, (state, action) => {
        state.loading = false
        state.adminRatings = action.payload
      })
      .addCase(getAllRatingsAdmin.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // VENDOR RATINGS
      .addCase(getVendorRatings.fulfilled, (state, action) => {
        state.vendorRatings = action.payload
      })
  }
})

export default ratingSlice.reducer
